import { Injectable } from "@angular/core";
import { IsPaginated, Paginated } from "@common/interfaces/base";
import { IBatchActivity } from "@common/interfaces/batchActivity";
import { Topics } from "@common/interfaces/topics";
import { SocketIoService } from '@ep-om/core/services/socket-io.service';
import { concatMap, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AuthQuery } from "../auth/auth.query";
import { ActivityService } from "../service/activity.service";
import { LastUpdateQuery } from './lastUpdate/lastUpdate.query';
import { LastUpdateService } from './lastUpdate/lastUpdate.service';
import { ProjectQuery } from './project/project.query';
import { UpdateStoreStrategy } from "./updateStoreStrategies";
import { EntityResponse, isEntityResponse } from "@common/api/ack";

@Injectable({
  providedIn: 'root',
})
export class ProjectBatchActivityStrategy extends UpdateStoreStrategy {
  constructor(
    protected projectQuery: ProjectQuery,
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService,
    private activityService: ActivityService,
  ) { super() }
  isStoreLive$(): Observable<boolean> { return of(true); }
  start(topic: Topics) {
    console.log('starting ', topic);
    this.socketIoService.uponConnection$.pipe(
      switchMap(() => this.projectQuery.activeId$.pipe(
        filter(projectId => !!projectId),
        distinctUntilChanged(),
        tap((projectId) => this.lastUpdateService.setStoreInSync(topic, projectId)),
        concatMap(async projectId => {
          const getUpdates = async () => {
            const lastUpdates = this.lastUpdateQuery.getValue();
            const key = `PRJ_${projectId}_${topic}`;
            const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
            return await this.socketIoService.sendRequest<Paginated<EntityResponse<IBatchActivity[]>>>(topic, 'first_sync', {
              filters: {
                projectId, updatedAt: lastUpdateQueryParams?.lastUpdate, lastId: lastUpdateQueryParams?.lastId
              }
            });
          }
          for (let updates = await getUpdates(); updates.body.remaining > 0; updates = await getUpdates()) {
            this.updateStore(topic, { topic, body: updates.body });
            console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic, 'project', projectId);
          }
          console.log('[FIRST SYNC] - completed -', topic, 'project', projectId);
          return projectId;
        }),
        tap((projectId) => this.lastUpdateService.setStoreLive(topic, projectId)),
        switchMap((projectId: string) => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const key = `PRJ_${projectId}_${topic}`;
          return this.socketIoService.listenEntity<IBatchActivity>(topic, { projectId, updatedAt: lastUpdates[key]?.lastUpdate, lastId: lastUpdates[key]?.lastId })
        }),
      )),
      this.socketIoService.stopOnDisconnectionPipe(),
      this.socketIoService.repeatOnConnectionPipe(),
    ).subscribe(
      updates => this.updateStore(topic, { topic: updates.topic, body: updates.data })
    )
  }
  updateStore(topic: Topics, updates: { topic: Topics, body: Paginated<EntityResponse<IBatchActivity[]>> | IBatchActivity[] }) {
    const [activities, remaining] = IsPaginated(updates.body) ?
      [updates.body.data.entities, updates.body.remaining]
      :
      [updates.body, null];
    if (activities.length === 0) {
      return;
    }
    this.activityService.executeActivities(activities);
    const lastItem = activities.at(-1);
    this.lastUpdateService.setChildLastUpdate(lastItem.projectId, updates.topic, {
      lastId: lastItem.id,
      lastUpdate: lastItem.updatedAt,
      ...(remaining && { remaining }),
    });
  }
}


@Injectable({
  providedIn: 'root',
})
export class GlobalBatchActivityStrategy extends UpdateStoreStrategy {
  constructor(
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService,
    private activityService: ActivityService,
  ) { super() }
  isStoreLive$(): Observable<boolean> { return of(true); }

  start(topic: Topics) {
    console.log('starting ', topic);
    this.socketIoService.uponConnection$.pipe(
      tap(() => this.lastUpdateService.setStoreInSync(topic)),
      concatMap(async () => {
        const getUpdates = async () => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const lastUpdateQueryParams = lastUpdates ? lastUpdates[topic] : null
          return await this.socketIoService.sendRequest<Paginated<EntityResponse<IBatchActivity[]>>>(topic, 'first_sync', {
            filters: {
              updatedAt: lastUpdateQueryParams?.lastUpdate, lastId: lastUpdateQueryParams?.lastId
            }
          });
        }
        for (let updates = await getUpdates(); updates.body.remaining > 0; updates = await getUpdates()) {
          this.updateStore(topic, { topic, body: updates.body });
          console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic, 'project');
        }
      }),
      tap(() => this.lastUpdateService.setStoreLive(topic)),
      switchMap(() => {
        const lastUpdates = this.lastUpdateQuery.getValue();
        return this.socketIoService.listenEntity<IBatchActivity>(topic, { updatedAt: lastUpdates[topic]?.lastUpdate, lastId: lastUpdates[topic]?.lastId })
      }),
      this.socketIoService.stopOnDisconnectionPipe(),
      this.socketIoService.repeatOnConnectionPipe(),
    ).subscribe(
      updates => this.updateStore(topic, { topic: updates.topic, body: updates.data })
    )
  }
  updateStore(topic: Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IBatchActivity[]>> | IBatchActivity[]; }): void {
    const [activities, remaining] = IsPaginated(updates.body) ?
      [isEntityResponse(updates.body.data) ? updates.body.data.entities : updates.body.data, updates.body.remaining]
      :
      [updates.body, null];
    if (activities.length === 0) {
      return;
    }
    this.activityService.executeActivities(activities);
    const lastItem = activities.at(-1);
    this.lastUpdateService.setLastUpdate(updates.topic, {
      lastId: lastItem.id,
      lastUpdate: lastItem.updatedAt,
      ...(remaining && { remaining }),
    });
  }
}

@Injectable({
  providedIn: 'root',
})
export class UserBatchActivityStrategy extends UpdateStoreStrategy {
  constructor(
    protected lastUpdateQuery: LastUpdateQuery,
    protected lastUpdateService: LastUpdateService,
    protected socketIoService: SocketIoService,
    private activityService: ActivityService,
    private authQuery: AuthQuery,
  ) { super() }
  isStoreLive$(): Observable<boolean> { return of(true); }

  start(topic: Topics) {
    console.log('starting ', topic);
    this.socketIoService.uponConnection$.pipe(
      tap(() => {
        const userId = this.authQuery.getLoggedUserId();
        this.lastUpdateService.setStoreInSync(topic, userId);
      }),
      concatMap(async projectId => {
        const getUpdates = async () => {
          const lastUpdates = this.lastUpdateQuery.getValue();
          const userId = this.authQuery.getLoggedUserId();
          const key = `PRJ_${userId}_${topic}`;
          const lastUpdateQueryParams = lastUpdates ? lastUpdates[key] : null;
          return await this.socketIoService.sendRequest<Paginated<EntityResponse<IBatchActivity[]>>>(topic, 'first_sync', {
            filters: {
              projectId, updatedAt: lastUpdateQueryParams?.lastUpdate, lastId: lastUpdateQueryParams?.lastId
            }
          });
        }
        for (let updates = await getUpdates(); updates.body.remaining > 0; updates = await getUpdates()) {
          this.updateStore(topic, { topic, body: updates.body });
          console.log('[FIRST SYNC] - upserted -', updates.body.data.entities.length, 'entities - ', topic, 'project', projectId);
        }
        console.log('[FIRST SYNC] - completed -', topic, 'project', projectId);
        return projectId;
      }),
      switchMap(() => {
        const lastUpdates = this.lastUpdateQuery.getValue();
        const userId = this.authQuery.getLoggedUserId();
        const key = `PRJ_${userId}_${topic}`;
        return this.socketIoService.listenEntity<IBatchActivity>(topic, { updatedAt: lastUpdates[key]?.lastUpdate, lastId: lastUpdates[key]?.lastId })
      }),
      tap(() => {
        const userId = this.authQuery.getLoggedUserId();
        this.lastUpdateService.setStoreLive(topic, userId);
      }),
      this.socketIoService.stopOnDisconnectionPipe(),
      this.socketIoService.repeatOnConnectionPipe(),
    ).subscribe(
      updates => {
        console.log('updates for topic', topic, updates)
        if (updates && updates.data && updates.data.length > 0) {
          this.updateStore(topic, { topic: updates.topic, body: updates.data })
        }
      }
    )
  }
  updateStore(topic: Topics, updates: { topic: Topics; body: Paginated<EntityResponse<IBatchActivity[]>> | IBatchActivity[]; }): void {
    const [activities, remaining] = IsPaginated(updates.body) ?
      [isEntityResponse(updates.body.data) ? updates.body.data.entities : updates.body.data, updates.body.remaining]
      :
      [updates.body, null];
    if (activities.length === 0) {
      return;
    }
    this.activityService.executeActivities(activities);
    const lastItem = activities.at(-1);
    const userId = this.authQuery.getLoggedUserId();
    this.lastUpdateService.setChildLastUpdate(userId, updates.topic, {
      lastId: lastItem.id,
      lastUpdate: lastItem.updatedAt,
      ...(remaining && { remaining }),
    });
  }
}


